<script setup lang="ts">
/**
 * The kit's one modal dialog (I-2799 folded NxModalShell into it): a native
 * `<dialog>` with backdrop, centring, Escape, the close button and the
 * open/close animation; a header that moves the dialog; and, on by default,
 * edges and a corner that size it plus a maximize button in the head (or a
 * double click on it), the size remembered under a `storageKey`. A `title` (and `subtitle`) and a
 * width step (`size`) give the standard dialog; the `header` slot takes over
 * the head for a dialog that draws its own. The `banner` slot sits between the
 * head and the scrolling body (a summary that must stay visible, e.g. an error
 * summary); it is not part of the body's padding. A dialog passes it always:
 * while its content renders nothing (a summary without entries) the region is
 * gone, gap included, and its text never widens the dialog — it wraps at the
 * width the body gives. Long notices scroll within a bounded region, including
 * in a dialog at its natural size, so its body and footer remain usable.
 */
import { DismissIcon } from '@blpde/nx-icons';
import Maximize2 from 'lucide-vue-next/dist/esm/icons/maximize-2.js'; import Minimize2 from 'lucide-vue-next/dist/esm/icons/minimize-2.js';
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  useId,
  useSlots,
  useTemplateRef,
  watch,
  type Component,
  type VNode,
} from 'vue';
import NxIconButton from '../IconButton/NxIconButton.vue';
import { loadingStateInjectionKey } from '../Inputs/internal';
import { i18nInjectionKey } from '../internal';
import { useNxPaneState } from '../Settings/settingsSource';
import { useTableFocusClaim } from '../Table/focusScope';
import NxModalToastHost from '../Toast/NxModalToastHost.vue';
import { clampModalDragOffset, isModalDragHandleTarget, type ModalDragOffset } from './modalDrag';
import {
  clampModalShift,
  clampModalSize,
  MODAL_RESIZE_DRIVER,
  maxModalSize,
  modalResizeRoom,
  resizeModalSize,
  storedModalSize,
  type ModalResizeEdge,
  type ModalSize,
  type StoredModalSize,
} from './modalResize';
import { modalTransitionDuration } from './modalTransition';

const {
  title = '',
  subtitle = '',
  size = undefined,
  autoWidth = false,
  width = undefined,
  fill = false,
  flush = false,
  closeOnClickOutside = true,
  closeOnEscape = true,
  draggable = true,
  modal = true,
  autofocus = false,
  focusFirstField: focusAndSelectFirstField = false,
  escapeTakesBack = undefined,
  resizable: resizableProp = true,
  storageKey = '',
  maximizeLabel = undefined,
  restoreLabel = undefined,
} = defineProps<{
  /** The dialog's title in the head; it names the dialog. The `header` slot replaces it. */
  title?: string;
  /** A smaller line under the title. */
  subtitle?: string;
  /**
   * Body width step: `default` 500 px, `wide` 680 px, `xwide` 800 px,
   * `xxwide` 1920 px for working with wide tables, bounded by the window's
   * dialog margin rather than the ordinary 90vw cap. All steps are size-unit multiples.
   * Without it the content decides the width.
   *
   * `viewport` is a viewer (a document, an attachment): the panel fills the
   * window up to the dialog's margin — the size a resizable dialog maximizes
   * to — with a fixed height, and the body fills it as a column that does not
   * scroll itself (`min-height: 0`), so its content scrolls inside. Below
   * 48em it covers the whole screen, without margin or rounded corners. It
   * has no size to choose and nowhere to move: no edges, no maximize button,
   * no drag — `resizable`, `storageKey` and `draggable` do not apply.
   */
  size?: 'default' | 'wide' | 'xwide' | 'xxwide' | 'viewport';
  /** Content-driven body width with the `size` step (or `default`) as its minimum. */
  autoWidth?: boolean;
  /**
   * Body width in size-unit multiples, instead of a `size` step's (a composer
   * that is wider than `xwide` but not a table). Bounded by the panel like the
   * steps, and with `autoWidth` it is the minimum. Not for `viewport`.
   */
  width?: number;
  /**
   * The body fills the panel's height between head and footer and does not
   * scroll itself (`min-height: 0`, no overflow): its content scrolls inside —
   * an editor whose head, footer and own controls (a picker, a tab row) stay in
   * place while only a part of it scrolls. Give that content `flex: 1 1 auto;
   * min-height: 0` (or `NxTabs` with `fill`). Without it the body scrolls as a
   * whole. Width steps, sizing and dragging stay as they are.
   */
  fill?: boolean;
  /**
   * An edge-to-edge panel for a work area (a composer, an editor) that draws
   * its own surfaces: the panel has no padding, the head and the footer become
   * bands with their own padding, separated from the body by a hairline, and
   * the body takes the room between them without scrolling itself (its content
   * scrolls inside, as with `fill`). Off by default.
   */
  flush?: boolean;
  /** Whether a click outside of the modal (i.e. on the backdrop) closes it. Defaults to `true`. */
  closeOnClickOutside?: boolean;
  /** Whether Escape closes the modal. Defaults to `true`. */
  closeOnEscape?: boolean;
  /**
   * When true (default), the header can be dragged to reposition the panel.
   * Offset resets every time the modal opens.
   */
  draggable?: boolean;
  /**
   * When true (default), the dialog is modal: top layer, everything else inert.
   * Off, it is a window (a composer, a record) — same look, scrim included, but
   * shown with `show()`, so nothing outside is made inert. It sits just below
   * the shell's drawers and taskbar, which stay usable while it is open.
   * Escape still closes it, after any menu open inside it.
   */
  modal?: boolean;
  /**
   * Focus the first field of the body when the dialog opens, so a dialog that
   * asks for input is typeable straight away. Off by default: a dialog whose
   * text should be read first keeps the browser's focus (the close button).
   */
  autofocus?: boolean;
  /**
   * `autofocus`, and the field's text is selected, so typing replaces the
   * value (Excel's dialogs). Once the dialog is up it focuses the body's
   * `[autofocus]` element, otherwise its first enabled field, over the
   * browser's own choice (the head's close button). Off by default.
   */
  focusFirstField?: boolean;
  /**
   * Escape first takes back an inner step of the dialog — an entry edited in
   * place, a sub-form Excel would open as a dialog of its own. Called on an
   * Escape inside this dialog that nothing in it used already (a field that
   * handles Escape calls `preventDefault`; an open menu goes first). Return
   * `true` when there was a step to take back: the dialog then stays open, the
   * key goes no further (a dialog below it stays open too), and focus that was
   * lost with the step goes to the first `[data-escape-focus]` element in the
   * dialog. `false` lets Escape close the dialog as usual.
   */
  escapeTakesBack?: () => boolean;
  /**
   * The user sizes the dialog: handles on the four edges and the bottom-right
   * corner, plus a maximize/restore button in the head, which a double click on
   * the head toggles as well. The natural size (as
   * rendered unsized) is the minimum, the visible window minus the dialog's
   * own margin the maximum. On by default; `false` for a confirmation.
   */
  resizable?: boolean;
  /**
   * Key under which the chosen size is remembered: `shell.panes.<storageKey>`
   * of the user's settings (`useNxPaneState`, the account config path in
   * Connect), module key first. Without one the dialog still sizes but opens at
   * its natural size every time.
   */
  storageKey?: string;
  /** Name of the head button while not maximized. Wins over `common.modal.maximize`. */
  maximizeLabel?: string;
  /** Name of the head button while maximized. Wins over `common.modal.restore`. */
  restoreLabel?: string;
}>();
const open = defineModel<boolean>('open', { required: true });

const emit = defineEmits<{
  /** Immediately on close */
  (event: 'close'): void;
  /** Once the dialog has been shown — the fade-in may still be running */
  (event: 'modalOpened'): void;
  /** Once the dialog has been closed — the fade-out may still be running */
  (event: 'modalClosed'): void;
  /** The dialog was maximized (`true`) or left that state (`false`). */
  (event: 'update:maximized', maximized: boolean): void;
}>();

defineSlots<{
  /** The head's content, in place of `title` and `subtitle`. */
  header?: Component;
  /**
   * Optional controls rendered in the header row between the title and the
   * kit close button (help, …). Lives OUTSIDE the element that names
   * the dialog, so buttons placed here never leak into the accessible name.
   * Clicks on controls in here do not start a drag.
   */
  'header-actions'?: () => VNode[];
  /**
   * A notice between head and body (an error summary), outside the body's
   * padding; takes no room while its content renders nothing. Long notices
   * scroll within their own height limit instead of displacing the body.
   */
  banner?: () => VNode[];
  /**
   * The dialog's content; with `fill`, its content owns scrolling. `heightGrowth`
   * is the height added above the natural panel size, for a work area whose
   * minimum must remain usable even when the complete form needs scrolling.
   */
  body?: (props: { heightGrowth: number }) => VNode[];
  /** The dialog's buttons; receives a close() that dismisses the modal. */
  footer?: (props: { close: () => void }) => VNode;
  /**
   * Left-aligned footer area beside the right-aligned `footer` — for a
   * destructive or secondary action („Löschen“) apart from the dialog's main
   * buttons. Same `close()` as `footer`; on a narrow dialog the main buttons
   * wrap below it, still on the right.
   */
  'footer-start'?: (props: { close: () => void }) => VNode;
}>();

const slots = useSlots();
const dialogRef = useTemplateRef<HTMLDialogElement>('dialog-ref');
const modalRef = useTemplateRef<HTMLElement>('modal-ref');
const bodyRef = useTemplateRef<HTMLElement>('body-ref');
// Opened from a table, it belongs to that table's editing wherever it renders.
useTableFocusClaim(dialogRef, () => open.value);
const titleId = `nx-modal-title-${useId()}`;
const rendered = ref(open.value);
// Whether the dialog is currently shown with showModal(). Only then it carries
// its own toast host: a closed dialog is display:none and cannot show a popover.
const modalShown = ref(false);
const renderKey = ref(0);
let closingDialog: HTMLDialogElement | null = null;
let cleanupTimer: ReturnType<typeof setTimeout> | undefined;
let cleanupDeadline = 0;

/** The title prop names the dialog; a `header` slot of its own is named as a whole. */
const titled = computed(() => !!title && !slots.header);

const dragOffset = ref<ModalDragOffset>({ x: 0, y: 0 });
const isDragging = ref(false);

let dragPointerId: number | null = null;
let dragStartClient = { x: 0, y: 0 };
let dragOriginOffset: ModalDragOffset = { x: 0, y: 0 };

function resetDrag() {
  dragOffset.value = { x: 0, y: 0 };
  isDragging.value = false;
  dragPointerId = null;
}

function handleCloseClick() {
  if (!open.value) return;
  open.value = false;
  emit('close');
}

function onCancel(e: Event) {
  // Only react to cancel on the <dialog> itself (Escape / UA dismiss).
  // Nested controls can also fire bubbling `cancel` — notably
  // <input type="file"> when the user cancels the native file picker.
  // Closing on those would dismiss the whole modal (e.g. BDMS quick capture
  // dropzone → Abbrechen).
  if (e.target !== dialogRef.value) return;
  e.preventDefault();
  if (closeOnEscape) handleCloseClick();
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== 'Escape' || e.defaultPrevented) return;
  if (hasOpenPopover(dialogRef.value)) return;
  if (takeBackOnEscape(e)) return;
  // A dialog opened with show() gets no `cancel` on Escape. Stand in for it, and
  // let a menu or popover open inside the window take the Escape first, as the
  // close-watcher stack does for a modal dialog.
  if (modal) return;
  e.preventDefault();
  if (closeOnEscape) handleCloseClick();
}

/**
 * `escapeTakesBack`: the key is handled here, on keydown, and not in `cancel`.
 * A keydown the page cancels never turns into a `cancel` (nor, without a user
 * activation, does Chromium fire a cancelable one at all), and preventing the
 * keydown is what keeps the native dialog open — this one and any dialog
 * below it. Runs in the bubble phase, after the fields inside: one that used
 * Escape itself (a restore of its typing) has prevented it by then.
 */
function takeBackOnEscape(e: KeyboardEvent): boolean {
  const dialog = dialogRef.value;
  if (!escapeTakesBack || !dialog || e.isComposing) return false;
  // A dialog stacked inside this one's body takes back its own steps.
  if (!(e.target instanceof Element) || e.target.closest('dialog') !== dialog) return false;
  if (!escapeTakesBack()) return false;
  e.preventDefault();
  e.stopPropagation();
  // The step taken back usually held the focus (its field is gone after the
  // render). Hand it to the dialog's marked control, e.g. its New button.
  void nextTick(() => {
    const active = document.activeElement;
    if (active && active !== dialog && dialog.contains(active)) return;
    dialog.querySelector<HTMLElement>('[data-escape-focus]')?.focus();
  });
  return true;
}

function hasOpenPopover(root: HTMLElement | null): boolean {
  try {
    // A tooltip is a popover too, but nothing to dismiss first.
    return Boolean(root?.querySelector(':popover-open:not([role="tooltip"])'));
  } catch {
    // An engine without the pseudo-class has no popovers to protect either.
    return false;
  }
}

// Whether the current press STARTED on the backdrop. A `click` whose target is
// the dialog element can also be produced by a drag that began INSIDE the modal
// (text selection, a textarea resize handle) and was released over the backdrop
// — the click then bubbles to the nearest common ancestor, the dialog. Closing
// on that would wrongly dismiss the modal mid-interaction, so we additionally
// require that the press began on the backdrop.
let pointerDownOnBackdrop = false;

function onBackdropPointerDown(e: PointerEvent) {
  pointerDownOnBackdrop = e.target === dialogRef.value;
}

function onBackdropClick(e: MouseEvent) {
  if (!closeOnClickOutside) return;
  // Close only when the press both started and ended on the dialog itself
  // (backdrop area), not its children.
  if (e.target === dialogRef.value && pointerDownOnBackdrop) {
    handleCloseClick();
  }
}

function cancelCloseCleanup() {
  clearTimeout(cleanupTimer);
  cleanupTimer = undefined;
  closingDialog = null;
}

function removeClosedDialog(dialog: HTMLDialogElement) {
  if (open.value || closingDialog !== dialog || dialogRef.value !== dialog) return;
  cancelCloseCleanup();
  rendered.value = false;
}

// Native close() releases the page immediately (I-1305). Keep the slot mounted
// only for the fade, then discard its local state even if transitionend never
// arrives (0s, a cancelled transition, or a hidden tab; I-1362).
function scheduleCloseCleanup(dialog: HTMLDialogElement) {
  cancelCloseCleanup();
  closingDialog = dialog;
  const dialogStyle = getComputedStyle(dialog);
  // Every browser computes at least `0s`. An engine that computes no
  // transitions (jsdom) has no backdrop fade either, and would only log
  // "not implemented" for the pseudo-element lookup.
  const computesTransitions = dialogStyle.transitionDuration !== '';
  const duration = Math.max(
    modalTransitionDuration(dialogStyle),
    modalRef.value ? modalTransitionDuration(getComputedStyle(modalRef.value)) : 0,
    modal && computesTransitions
      ? modalTransitionDuration(getComputedStyle(dialog, '::backdrop'))
      : 0
  );
  cleanupDeadline = performance.now() + duration;
  if (duration === 0) {
    removeClosedDialog(dialog);
  } else {
    // Allow a rendering frame to finish before the fallback removes the DOM.
    cleanupTimer = setTimeout(() => removeClosedDialog(dialog), duration + 50);
  }
}

function onTransitionEnd(e: TransitionEvent) {
  // Backdrop events also target the dialog. Neither they nor a shorter dialog
  // transition may cut off a longer fade on the panel or backdrop.
  if (!closingDialog || e.target !== closingDialog || performance.now() < cleanupDeadline) return;
  removeClosedDialog(closingDialog);
}

// A maximized dialog fills the window: there is nowhere to move it.
const canDrag = computed(() => draggable && !maximized.value && !viewport.value);

function onHeaderPointerDown(e: PointerEvent) {
  if (!canDrag.value) return;
  if (e.button !== 0) return;
  if (!isModalDragHandleTarget(e.target)) return;
  if (!modalRef.value) return;

  isDragging.value = true;
  dragPointerId = e.pointerId;
  dragStartClient = { x: e.clientX, y: e.clientY };
  dragOriginOffset = { ...dragOffset.value };
  // Capture on the header so moves outside the panel still track.
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  e.preventDefault();
}

function onHeaderPointerMove(e: PointerEvent) {
  if (!isDragging.value || e.pointerId !== dragPointerId) return;
  const panel = modalRef.value?.getBoundingClientRect();
  if (!panel) return;

  // Undo current transform to get the flex-centred layout box, then apply the
  // candidate offset from the drag origin + pointer delta.
  const layout = {
    left: panel.left - dragOffset.value.x,
    top: panel.top - dragOffset.value.y,
    width: panel.width,
    height: panel.height,
  };
  const candidate: ModalDragOffset = {
    x: dragOriginOffset.x + (e.clientX - dragStartClient.x),
    y: dragOriginOffset.y + (e.clientY - dragStartClient.y),
  };
  dragOffset.value = clampModalDragOffset(candidate, layout, {
    width: window.innerWidth,
    height: window.innerHeight,
  });
}

function onHeaderPointerUp(e: PointerEvent) {
  if (e.pointerId !== dragPointerId) return;
  isDragging.value = false;
  dragPointerId = null;
  try {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  } catch {
    // already released
  }
}

/**
 * Fields the browser can type into. Buttons are deliberately absent: `showModal`
 * already focuses the first focusable element, which is the header's close
 * button — that is the behaviour `autofocus` replaces, not one to reproduce. A
 * dialog with no field therefore keeps the native focus.
 */
const FOCUSABLE_FIELD =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]';

/**
 * A radio group is one tab stop, and the keyboard lands on its checked radio —
 * not on the first one in document order, which arrow keys would then move
 * away from the chosen value.
 */
function checkedRadioOf(body: HTMLElement, field: HTMLElement | null): HTMLElement | null {
  if (!(field instanceof HTMLInputElement) || field.type !== 'radio' || !field.name) return field;
  const checked = [
    ...body.querySelectorAll<HTMLInputElement>('input[type="radio"]:checked:not([disabled])'),
  ].find((radio) => radio.name === field.name);
  return checked ?? field;
}

/** `autofocus`, and with `select` (`focusFirstField`) the field's text selected. */
function focusBodyField(select: boolean) {
  const body = bodyRef.value;
  if (!body) return;
  // An explicit `autofocus` in the slot wins — the consumer knows better.
  const target = checkedRadioOf(
    body,
    body.querySelector<HTMLElement>('[autofocus]') ??
      body.querySelector<HTMLElement>(FOCUSABLE_FIELD)
  );
  target?.focus();
  if (!select) return;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    try {
      target.select();
    } catch {
      // Not every input type selects; the focus is enough then.
    }
  }
}

// --- Sizing -----------------------------------------------------------------
// The panel keeps its centred layout and the header's drag offset (`transform`).
// A chosen size goes on the panel, plus a `translate` that keeps the edge
// opposite to the dragged one in place — a separate property from `transform`,
// so moving the dialog by its header keeps working untouched.

type Shift = { x: number; y: number };

const ZERO_SHIFT: Shift = { x: 0, y: 0 };
const RESIZE_HANDLES: ModalResizeEdge[] = ['n', 'e', 's', 'w', 'se'];

/** Chosen panel size; `null` = the natural size, nothing is set on the panel. */
const panelSize = ref<ModalSize | null>(null);
const shift = ref<Shift>(ZERO_SHIFT);
const maximized = ref(false);
const resizing = ref(false);

watch(maximized, (value) => emit('update:maximized', value));

// A label prop wins, then the kit translation (the host provides it), then
// German — a host without kit messages hands the key back, which is no label.
const i18n = inject(i18nInjectionKey, null);
function kitLabel(key: string, fallback: string): string {
  const text = i18n?.t(key);
  return text && text !== key ? text : fallback;
}
const toggleLabel = computed(() =>
  maximized.value
    ? (restoreLabel ?? kitLabel('common.modal.restore', 'Wiederherstellen'))
    : (maximizeLabel ?? kitLabel('common.modal.maximize', 'Maximieren'))
);

/** The panel as it renders without a custom size — measured on open, the minimum. */
let natural: ModalSize | null = null;
/** What "Wiederherstellen" returns to; `null` size = the natural one. */
let restoreState: { size: ModalSize | null; shift: Shift; drag: ModalDragOffset } | null = null;
let resizeDrag: {
  pointerId: number;
  edge: ModalResizeEdge;
  startClient: { x: number; y: number };
  startSize: ModalSize;
  startShift: Shift;
  /** Extent the dragged edge may reach before it leaves the visible area. */
  room: { width: number; height: number };
  moved: boolean;
} | null = null;

/** A viewer fills the window already: nothing to size (see `size`). */
const viewport = computed(() => size === 'viewport');
const resizable = computed(() => resizableProp && !viewport.value);
const sized = computed(() => resizable.value && panelSize.value !== null);

function measurePanel(panel: HTMLElement): ModalSize {
  // offsetWidth/Height ignore the open/close scale transform.
  return { width: panel.offsetWidth, height: panel.offsetHeight };
}

/** The margin the dialog host keeps around its panel — the kit's own token (--nxt-space-sm). */
function hostMargin(): number {
  const dialog = dialogRef.value;
  return dialog ? Number.parseFloat(getComputedStyle(dialog).paddingLeft) || 0 : 0;
}

/** Largest size: the viewport minus that margin on every side. */
function currentMaxSize(): ModalSize {
  return maxModalSize({ width: window.innerWidth, height: window.innerHeight }, hostMargin());
}

function sameSize(a: ModalSize, b: ModalSize): boolean {
  return a.width === b.width && a.height === b.height;
}

function resetResizing() {
  panelSize.value = null;
  shift.value = ZERO_SHIFT;
  maximized.value = false;
  resizing.value = false;
  natural = null;
  restoreState = null;
  resizeDrag = null;
}

// The remembered size: the user's settings, `shell.panes.<storageKey>` — nothing without a key
// or when the dialog is not resizable.
const pane = useNxPaneState<StoredModalSize>(() => (resizable.value ? storageKey : ''));

function writeStoredSize() {
  if (!resizable.value || !storageKey) return;
  const normal = maximized.value ? (restoreState?.size ?? natural) : (panelSize.value ?? natural);
  if (!normal) return;
  pane.patch({ width: normal.width, height: normal.height, maximized: maximized.value });
}

/** Pull the panel back inside the window after a size change that was not a drag. */
function keepPanelInside() {
  if (!panelSize.value) return;
  shift.value = clampModalShift(shift.value, dragOffset.value, panelSize.value, {
    viewport: { width: window.innerWidth, height: window.innerHeight },
    marginPx: hostMargin(),
  });
}

/** Fill the window, centred: the header's drag offset is set aside until restore. */
function applyMaximize() {
  dragOffset.value = { x: 0, y: 0 };
  panelSize.value = currentMaxSize();
  shift.value = ZERO_SHIFT;
  maximized.value = true;
}

// Measure the natural size first, then apply what was remembered: the stored
// size is only ever a candidate between natural and maximum.
function applyStoredSize() {
  const panel = modalRef.value;
  if (!resizable.value || !panel) return;
  natural = measurePanel(panel);
  const stored = storedModalSize(pane.state.value);
  if (!stored) return;
  const normal = clampModalSize(stored, natural, currentMaxSize());
  const keepNatural = sameSize(normal, natural);
  if (stored.maximized) {
    restoreState = { size: keepNatural ? null : normal, shift: ZERO_SHIFT, drag: { x: 0, y: 0 } };
    applyMaximize();
  } else if (!keepNatural) {
    panelSize.value = normal;
    keepPanelInside();
  }
}

function toggleMaximize() {
  const panel = modalRef.value;
  if (!panel) return;
  if (maximized.value) {
    const back = restoreState;
    restoreState = null;
    maximized.value = false;
    panelSize.value = back?.size
      ? clampModalSize(back.size, natural ?? back.size, currentMaxSize())
      : null;
    shift.value = back?.shift ?? ZERO_SHIFT;
    dragOffset.value = back?.drag ?? { x: 0, y: 0 };
    keepPanelInside();
  } else {
    // Unsized, the panel IS the natural size — take a fresh measure, the content
    // may have changed since it opened.
    if (!panelSize.value) natural = measurePanel(panel);
    restoreState = { size: panelSize.value, shift: shift.value, drag: dragOffset.value };
    applyMaximize();
  }
  writeStoredSize();
}

/**
 * A double click on the head maximizes or restores, like a window's title bar.
 * Buttons, fields and `data-no-drag` parts of the head keep their own clicks.
 */
function onHeaderDoubleClick(e: MouseEvent) {
  if (!resizable.value || e.button !== 0) return;
  if (!isModalDragHandleTarget(e.target)) return;
  toggleMaximize();
}

function onHandlePointerDown(e: PointerEvent, edge: ModalResizeEdge): ModalSize | null {
  if (e.button !== 0) return null;
  const panel = modalRef.value;
  if (!panel) return null;
  const current = measurePanel(panel);
  // Unsized, the panel IS the natural size: take a fresh measure as the minimum.
  if (!panelSize.value || !natural) natural = current;
  resizeDrag = {
    pointerId: e.pointerId,
    edge,
    startClient: { x: e.clientX, y: e.clientY },
    startSize: current,
    startShift: shift.value,
    room: modalResizeRoom(
      edge,
      panel.getBoundingClientRect(),
      { width: window.innerWidth, height: window.innerHeight },
      hostMargin()
    ),
    moved: false,
  };
  resizing.value = true;
  try {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  } catch {
    // not capturable (synthetic event): the moves still arrive from the handle
  }
  // Keeps the press from starting a text selection or the backdrop-close logic.
  e.preventDefault();
  return current;
}

function onHandlePointerMove(e: PointerEvent): ModalSize | null {
  if (!resizeDrag || e.pointerId !== resizeDrag.pointerId || !natural) return null;
  if (maximized.value) {
    // Dragging an edge leaves the maximized state; the new size is the normal one.
    maximized.value = false;
    restoreState = null;
  }
  resizeDrag.moved = true;
  const result = resizeModalSize(
    resizeDrag.edge,
    resizeDrag.startSize,
    { x: e.clientX - resizeDrag.startClient.x, y: e.clientY - resizeDrag.startClient.y },
    { min: natural, max: currentMaxSize(), room: resizeDrag.room }
  );
  panelSize.value = result.size;
  shift.value = {
    x: resizeDrag.startShift.x + result.shift.x,
    y: resizeDrag.startShift.y + result.shift.y,
  };
  return result.size;
}

function onHandlePointerUp(e: PointerEvent) {
  if (!resizeDrag || e.pointerId !== resizeDrag.pointerId) return;
  const moved = resizeDrag.moved;
  resizeDrag = null;
  resizing.value = false;
  try {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  } catch {
    // already released
  }
  if (moved) writeStoredSize();
}

// A smaller window pulls the dialog in, a bigger one lets a maximized dialog follow.
function onWindowResize() {
  if (!open.value || !resizable.value || !panelSize.value) return;
  const max = currentMaxSize();
  panelSize.value = maximized.value
    ? max
    : clampModalSize(panelSize.value, natural ?? panelSize.value, max);
  // The shift was right for the old size and window: re-clamp it, or a panel
  // that is narrower than before still sticks out on the shifted side.
  keepPanelInside();
}

// A grip inside the body (NxModalResizeGrip, e.g. on a text field) drives the same path as the
// corner handle: the field drag sizes the modal, top-left anchored, and is remembered.
const heightGrowth = computed(() =>
  panelSize.value && natural ? Math.max(0, panelSize.value.height - natural.height) : 0
);
// Each dialog is its own error context: its inputs never take the save status of the field the
// dialog sits in (a field's image dialog would otherwise mark every input in it).
provide(loadingStateInjectionKey, null);
provide(MODAL_RESIZE_DRIVER, {
  enabled: () => resizable.value,
  isOpen: () => open.value,
  growth: () => heightGrowth.value,
  start: onHandlePointerDown,
  move: onHandlePointerMove,
  end: onHandlePointerUp,
});

const panelStyle = computed(() => ({
  // CSS vars drive translate so close-animation scale can compose with drag.
  '--modal-drag-x': `${dragOffset.value.x}px`,
  '--modal-drag-y': `${dragOffset.value.y}px`,
  ...(sized.value && panelSize.value
    ? {
        width: `${panelSize.value.width}px`,
        height: `${panelSize.value.height}px`,
        minWidth: '0',
        maxWidth: 'none',
        maxHeight: 'none',
        '--modal-resize-shift-x': `${shift.value.x}px`,
        '--modal-resize-shift-y': `${shift.value.y}px`,
      }
    : {}),
}));

const bodyStyle = computed(() => {
  // A sized panel decides the body's width and height.
  if (sized.value) return { width: 'auto', minWidth: '0' } as const;
  // A viewer's panel decides it too (CSS, `.is-viewport`).
  if (size === 'viewport' || (!size && !autoWidth && width === undefined)) return undefined;
  // Size-unit multiples so width steps follow the scale, bounded by the panel.
  const units = width ?? { default: 31.25, wide: 42.5, xwide: 50, xxwide: 120 }[size ?? 'default'];
  const base = `calc(var(--nxt-size-unit) * ${units})`;
  // A step never pushes the body past what the window leaves the panel.
  if (autoWidth) return { width: 'fit-content', minWidth: `min(${base}, 100%)` } as const;
  // The step sizes the panel; a wider head or footer (a long hint) widens it
  // further, and the body then spans that width too — it does from the first
  // resize on (`width: auto` above), so it must at open as well. The percentage
  // is ignored while the panel measures its content, so it never widens it.
  return { width: base, minWidth: '100%' } as const;
});

watch(
  [open, dialogRef],
  async ([isOpen, dialog]) => {
    if (isOpen) {
      // A quick reopen can beat both transitionend and the fallback timer.
      // Replace that closing instance so its slot state cannot survive.
      if (closingDialog) renderKey.value += 1;
      cancelCloseCleanup();
      resetDrag();
      // Every open starts from the natural size; the stored one follows once
      // the natural size can be measured.
      resetResizing();
      rendered.value = true;
      await nextTick();
      // `open` can flip back to false across either await — this callback is
      // async and nothing re-checks it for us. Without these two guards a
      // close landing in between would already have emitted `modalClosed`,
      // and this continuation would then emit `modalOpened` AFTER it, sending
      // the three focus handlers into a dialog that is closed and
      // `display: none`. Found in review round 1 on I-1305.
      if (!open.value) return;
      const activeDialog = dialogRef.value;
      if (activeDialog && !activeDialog.open) {
        if (modal) activeDialog.showModal();
        else activeDialog.show();
        modalShown.value = modal;
        // One tick so consumers focusing from `modalOpened` find the dialog
        // laid out.
        await nextTick();
        if (!open.value || activeDialog !== dialogRef.value) return;
        applyStoredSize();
        emit('modalOpened');
        if (autofocus || focusAndSelectFirstField) focusBodyField(focusAndSelectFirstField);
      }
    } else if (dialog?.open) {
      modalShown.value = false;
      // Close NOW. This drops the top layer and the inert lock on the rest of
      // the page in the very same tick, so the close can no longer depend on an
      // event arriving. The fade-out is not lost: `display` and `overlay`
      // transition with `allow-discrete`, which keeps the element rendered and
      // in the top layer until the opacity transition is over.
      dialog.close();
      emit('modalClosed');
      scheduleCloseCleanup(dialog);
    } else {
      modalShown.value = false;
      cancelCloseCleanup();
      rendered.value = false;
    }
  },
  { flush: 'post', immediate: true }
);

onMounted(() => window.addEventListener('resize', onWindowResize));
onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize);
  cancelCloseCleanup();
  dialogRef.value?.close();
  rendered.value = false;
});

defineOptions({ inheritAttrs: false });
</script>

<template>
  <dialog
    v-if="rendered"
    :key="renderKey"
    ref="dialog-ref"
    class="modal-dialog"
    :class="{ 'is-window': !modal, 'is-viewport': viewport }"
    :aria-labelledby="titleId"
    @cancel="onCancel"
    @keydown="onKeyDown"
    @pointerdown="onBackdropPointerDown"
    @click="onBackdropClick"
    @transitionend="onTransitionEnd"
  >
    <div
      ref="modal-ref"
      class="modal"
      :class="{
        dragging: isDragging,
        'is-resizable': resizable,
        'is-sized': sized,
        'is-resizing': resizing,
        'is-maximized': maximized,
        'is-viewport': viewport,
        'is-xxwide': size === 'xxwide',
        'is-fill': fill,
        'is-flush': flush,
      }"
      :style="panelStyle"
      v-bind="$attrs"
    >
      <div
        class="header"
        :class="{ draggable: canDrag }"
        @pointerdown="onHeaderPointerDown"
        @pointermove="onHeaderPointerMove"
        @pointerup="onHeaderPointerUp"
        @pointercancel="onHeaderPointerUp"
        @dblclick="onHeaderDoubleClick"
      >
        <div :id="titled ? undefined : titleId" class="title">
          <slot name="header">
            <div v-if="title || subtitle" class="nx-modal__head">
              <div :id="titleId" class="nx-modal__title">{{ title }}</div>
              <div v-if="subtitle" class="nx-modal__subtitle">{{ subtitle }}</div>
            </div>
          </slot>
        </div>
        <div v-if="$slots['header-actions'] || resizable" class="header-actions" data-no-drag>
          <slot name="header-actions"></slot>
          <NxIconButton
            v-if="resizable"
            height="xs"
            data-test="maximize-toggle"
            :title="toggleLabel"
            :aria-label="toggleLabel"
            @click="toggleMaximize"
          >
            <Minimize2 v-if="maximized" class="nxt-icon-sm" aria-hidden="true" />
            <Maximize2 v-else class="nxt-icon-sm" aria-hidden="true" />
          </NxIconButton>
        </div>
        <button
          type="button"
          class="close-btn"
          data-no-drag
          aria-label="Close dialog"
          @click="handleCloseClick"
        >
          <DismissIcon class="nxt-icon-sm" aria-hidden="true" />
        </button>
      </div>
      <div v-if="$slots.banner" class="nx-modal__banner">
        <slot name="banner"></slot>
      </div>
      <div ref="body-ref" class="body" :style="bodyStyle">
        <slot name="body" :height-growth="heightGrowth"></slot>
      </div>
      <div
        v-if="$slots.footer || $slots['footer-start']"
        class="footer"
        :class="{ 'has-start': $slots['footer-start'] }"
      >
        <div v-if="$slots['footer-start']" class="footer-start">
          <slot name="footer-start" :close="handleCloseClick"></slot>
        </div>
        <div v-if="$slots['footer-start'] && $slots.footer" class="footer-end">
          <slot name="footer" :close="handleCloseClick"></slot>
        </div>
        <slot v-else name="footer" :close="handleCloseClick"></slot>
      </div>
      <template v-if="resizable">
        <div
          v-for="edge in RESIZE_HANDLES"
          :key="edge"
          class="nx-modal__resize"
          :class="`nx-modal__resize--${edge}`"
          :data-resize-edge="edge"
          data-no-drag
          aria-hidden="true"
          @pointerdown="onHandlePointerDown($event, edge)"
          @pointermove="onHandlePointerMove"
          @pointerup="onHandlePointerUp"
          @pointercancel="onHandlePointerUp"
        ></div>
      </template>
    </div>
    <!-- showModal() makes everything outside the dialog inert, the app's toast
         host included: a press on a toast would fall through to the backdrop and
         close the modal. While the dialog is open it carries its own host — the
         newest host renders the shared toasts, looking like the page's — and the
         page's takes over on close. -->
    <NxModalToastHost v-if="modal && open && modalShown" />
  </dialog>
</template>

<style scoped>
.modal-dialog {
  /* Full-viewport flex host: the <dialog> itself is the hit-target for
     backdrop clicks (`e.target === dialog`), while the white panel is
     centred with flex — more reliable than inset+margin:auto, which
     often left the panel top-centre after margin resets / fit-content
     quirks (Spalten anpassen). */
  position: fixed;
  inset: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  border: none;
  background: transparent;
  padding: var(--nxt-space-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  opacity: 1;
  /* `display` and `overlay` are discrete properties; `allow-discrete` lets them
     take part in the transition, and the browser then keeps the closed dialog
     rendered AND in the top layer until the opacity fade has finished. That is
     what makes the synchronous `close()` in the watcher safe: the lock on the
     page falls at once, the fade-out still plays. Drop the `overlay` line and
     the element leaves the top layer immediately — the fade-out is skipped.
     `transition-behavior: allow-discrete` and `@starting-style` below have been
     Baseline since 2024; the shipped runtime is Electron 35 / Chromium 134. */
  transition:
    opacity var(--nxt-motion-fast) ease,
    display var(--nxt-motion-fast) allow-discrete,
    overlay var(--nxt-motion-fast) allow-discrete;
  outline: none;
  box-sizing: border-box;
}

/* End state of the fade-out — and the state a dialog that is merely rendered
   but not shown sits in. `display: none` matters: the host is
   `position: fixed; inset: 0`, so a closed-but-displayed dialog would keep
   swallowing pointer events across the whole viewport even at `opacity: 0`.
   `pointer-events` is deliberately NOT part of the transition above: it takes
   effect the moment the dialog closes, so the element stops being a hit target
   right away instead of at the end of the fade — and stays harmless even where
   the transition never advances (a hidden or throttled tab, measured 27.08.). */
.modal-dialog:not([open]) {
  display: none;
  opacity: 0;
  pointer-events: none;
}

.modal-dialog::backdrop {
  background-color: var(--nxt-modal-overlay-bg);
  opacity: 1;
  transition:
    opacity var(--nxt-motion-fast) ease,
    display var(--nxt-motion-fast) allow-discrete,
    overlay var(--nxt-motion-fast) allow-discrete;
}

.modal-dialog:not([open])::backdrop {
  opacity: 0;
}

/* A window (`modal` off) is not in the top layer and has no ::backdrop: the
   full-viewport host is the scrim, fading with it. It stacks just below the
   shell's drawers and taskbar so those stay usable above it. */
.modal-dialog.is-window {
  z-index: var(--nxt-z-drawer-backdrop);
  background-color: var(--nxt-modal-overlay-bg);
}

/* A dialog without `[open]` is `display: none`, so every showModal() is a
   discrete step into the rendered state — this supplies the value to start the
   fade-in from, on every open and not just the first. */
@starting-style {
  .modal-dialog[open] {
    opacity: 0;
  }

  .modal-dialog[open]::backdrop {
    opacity: 0;
  }
}

.modal-dialog .modal {
  transition: transform var(--nxt-motion-fast) ease;
  transform: translate(var(--modal-drag-x, 0), var(--modal-drag-y, 0)) scale(1);
}

.modal-dialog:not([open]) .modal {
  transform: translate(var(--modal-drag-x, 0), var(--modal-drag-y, 0)) scale(0.95);
}

.modal {
  /* Anchor for the resize handles. */
  position: relative;
  background: var(--nxt-modal-bg);
  /* Teleported to the top layer: never inherit the page's text colour. */
  color: var(--nxt-modal-fg);
  border: var(--nxt-hairline) solid var(--nxt-modal-border);
  border-radius: var(--nxt-radius-lg);
  box-shadow: var(--nxt-modal-shadow);
  padding: var(--nxt-space-md);
  display: flex;
  flex-direction: column;
  /* Shrink-wrap body content — never stretch to the full viewport. */
  width: fit-content;
  min-width: min(var(--nxt-panel-width-lg), 90vw);
  max-width: min(90vw, 100%);
  max-height: min(90vh, 100%);
  margin: 0;
  overflow: auto;
  box-sizing: border-box;
  /* Keep the panel a flex item that doesn't grow to fill the host. */
  flex: 0 1 auto;
}

/* Wide table work can use the host's whole width, keeping its outer margin. */
.modal.is-xxwide {
  max-width: 100%;
}

/* Keeps the edge opposite to a dragged one in place (see script). A separate
   property from the drag's `transform`, so moving the dialog still works.
   Nothing transitions size or shift — reduced motion has nothing to switch off. */
.modal.is-sized {
  translate: var(--modal-resize-shift-x, 0) var(--modal-resize-shift-y, 0);
  overflow: hidden;
}

/* `fill`: the panel itself never scrolls; the body takes the room between head
   and footer and hands it on (below). */
.modal.is-fill {
  overflow: hidden;
}

/* A viewer (`size="viewport"`): the host's content box is the window minus
   its margin (the padding above) — the size a resizable dialog maximizes to. */
.modal.is-viewport {
  width: 100%;
  height: 100%;
  min-width: 0;
  max-width: none;
  max-height: none;
  overflow: hidden;
}

/* `flush`: no panel padding; head and footer are padded bands set off from the
   body by a hairline, and the body hands its height on without scrolling. */
.modal.is-flush {
  padding: 0;
  overflow: hidden;
}

.modal.is-flush > .header {
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}

.modal.is-flush > .body {
  min-height: 0;
  overflow: hidden;
}

.modal.is-flush > .footer {
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
}

/* Its body fills the panel and hands the height on: the content scrolls
   inside, the body itself does not. The same for `fill`. */
.modal.is-viewport > .body,
.modal.is-fill > .body {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

/* On a narrow screen a viewer takes all of it: no margin, no corners. */
@media (max-width: 48em) {
  .modal-dialog.is-viewport {
    padding: 0;
  }

  .modal.is-viewport {
    border: none;
    border-radius: 0;
  }
}

.modal.dragging,
.modal.is-resizing {
  transition: none;
  user-select: none;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* The title leads: one step above the body text. */
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  padding-bottom: var(--nxt-space-sm);
  /* room for grab without eating the close button hit area */
  gap: var(--nxt-space-xs);
  touch-action: none;
  flex: 0 0 auto;
}

.header.draggable {
  cursor: grab;
}

.header .title {
  flex: 1 1 auto;
  min-width: 0;
}

.nx-modal__head {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

.nx-modal__title {
  color: var(--nxt-fg);
}

.nx-modal__subtitle {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-regular);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-muted);
}

.header-actions {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  flex-shrink: 0;
  cursor: default;
}

.modal.dragging .header.draggable {
  cursor: grabbing;
}

/* Between head and body, outside the scroll: it keeps its own height, the body
   takes the rest. `contain: inline-size` keeps its text out of the panel's
   `fit-content` width: a long line wraps at the width the body gives instead
   of stretching the dialog to 90vw. */
.nx-modal__banner {
  flex: 0 0 auto;
  /* 240 px at standard scale, less in a short viewport. Unlike a percentage
     height, this also bounds a dialog whose height is content-driven. */
  max-height: min(calc(var(--nxt-size-unit) * 15), 40vh);
  padding-bottom: var(--nxt-space-sm);
  overflow-y: auto;
  box-sizing: border-box;
  contain: inline-size;
}

/* Content that renders nothing (a summary without entries) leaves only Vue's
   comment and empty anchors, which `:empty` ignores: no region, no gap. */
.nx-modal__banner:empty {
  display: none;
}

/* A panel of fixed height also reserves at least 60% for its other regions. */
.modal.is-sized > .nx-modal__banner,
.modal.is-viewport > .nx-modal__banner {
  max-height: min(calc(var(--nxt-size-unit) * 15), 40%);
}

/* The content scrolls between head and footer; a sized panel hands it its height. */
.body {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  max-width: 100%;
  min-height: 0;
  overflow-y: auto;
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}

.modal.is-sized > .body {
  flex: 1 1 auto;
  min-width: 0;
}

/* The global type reset sizes every bare <p>/<ul> at 16px medium; inside the
   dialog the body sets the size. */
.body :deep(:is(p, ul, ol)) {
  font-size: inherit;
  font-weight: inherit;
}

.footer {
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  align-items: center;
  padding-top: var(--nxt-space-md);
  gap: var(--nxt-space-sm);
}

/* With a start area the two groups sit apart and wrap as whole groups: the
   main buttons keep to the right on their own line in a narrow dialog. */
.footer.has-start {
  flex-wrap: wrap;
  justify-content: space-between;
}

.footer-start,
.footer-end {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-sm);
}

.footer-end {
  justify-content: flex-end;
  margin-inline-start: auto;
}

.close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--nxt-control-size-xs);
  height: var(--nxt-control-size-xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-muted);
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 0;
  &:hover {
    color: var(--nxt-fg);
    background: var(--nxt-surface-sunken-bg, transparent);
  }
}

.nx-modal__resize {
  position: absolute;
  z-index: 1;
  touch-action: none;
}

.nx-modal__resize--n,
.nx-modal__resize--s {
  left: 0;
  right: 0;
  height: var(--nxt-space-xs);
  cursor: ns-resize;
}

.nx-modal__resize--e,
.nx-modal__resize--w {
  top: 0;
  bottom: 0;
  width: var(--nxt-space-xs);
  cursor: ew-resize;
}

.nx-modal__resize--n {
  top: 0;
}

.nx-modal__resize--s {
  bottom: 0;
}

.nx-modal__resize--e {
  right: 0;
}

.nx-modal__resize--w {
  left: 0;
}

.nx-modal__resize--se {
  right: 0;
  bottom: 0;
  z-index: 2;
  width: var(--nxt-space-md);
  height: var(--nxt-space-md);
  cursor: nwse-resize;
  /* A diagonal grip in the corner so the handle can be found. */
  background: linear-gradient(
    135deg,
    transparent 55%,
    var(--nxt-border-strong) 55%,
    var(--nxt-border-strong) 65%,
    transparent 65%,
    transparent 80%,
    var(--nxt-border-strong) 80%,
    var(--nxt-border-strong) 90%,
    transparent 90%
  );
}
</style>
